import { useState, useEffect, useMemo } from 'react';
import { collection, query, onSnapshot, where, doc, getDocs, orderBy, limit } from 'firebase/firestore';
import { deepEqual } from '../utils';
import { useLocation } from 'react-router-dom';
import { db } from '../firebase';
import { appId } from '../constants';

const CONCERTS_CACHE_TTL_MS = 24 * 60 * 60 * 1000;
const CONCERTS_CACHE_KEY = 'nytm_concerts_cache_v1';


const stableSet = (setter, newVal, useDeep = false) => {
  setter(prev => {
    if (useDeep) {
      if (deepEqual(prev, newVal)) return prev;
    } else {
      if (JSON.stringify(prev) === JSON.stringify(newVal)) return prev;
    }
    return newVal;
  });
};

export const useProjectData = (user, isVault) => {
  const [channels, setChannels] = useState([]);
  const [deletedChannels, setDeletedChannels] = useState([]);
  const [allTasks, setAllTasks] = useState([]);
  const [events, setEvents] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [habits, setHabits] = useState([]);
  const [habitLogs, setHabitLogs] = useState([]);
  const [photos, setPhotos] = useState([]);
  const [audioTracks, setAudioTracks] = useState([]);
  const [songkickConcerts, setSongkickConcerts] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const location = useLocation();

  useEffect(() => {
    if (!isVault && !user) return;
    const uid = user?.uid || 'test-uid-123';

    const q = query(
      collection(db, 'artifacts', appId, 'public', 'data', 'habits'),
      where('createdBy', '==', uid)
    );
    const unsub = onSnapshot(q, (snap) => {
      setHabits(snap.docs.map((d) => ({ ...d.data(), id: d.id })));
    });
    return () => unsub();
  }, [user, isVault]);

  useEffect(() => {
    if (!isVault && !user) return;
    const uid = user?.uid || 'test-uid-123';

    const q = query(
      collection(db, 'artifacts', appId, 'public', 'data', 'habitLogs'),
      where('uid', '==', uid)
    );
    const unsub = onSnapshot(q, (snap) => {
      setHabitLogs(snap.docs.map((d) => ({ ...d.data(), id: d.id })));
    });
    return () => unsub();
  }, [user, isVault]);

  useEffect(() => {
    if (!isVault && !user) return;
    const uid = user?.uid || 'test-uid-123';

    // Split 'or' query into 3 separate listeners to avoid index requirement
    let channelsPart = [];
    let channelsCreated = [];
    let channelGlobal = [];

    const updateChannels = () => {
      const all = [...channelsPart, ...channelsCreated, ...channelGlobal];
      const unique = Array.from(new Map(all.map((c) => [c.id, c])).values());
      unique.sort((a, b) => (a.order || 0) - (b.order || 0));

      if (isVault && !unique.find((c) => c.id === 'general_chat_global')) {
        unique.push({
          id: 'general_chat_global',
          name: 'general',
          order: 0,
          createdAt: { seconds: 0 },
          createdBy: 'system',
        });
      }
      setChannels(unique);
    };

    const q1 = query(
      collection(db, 'artifacts', appId, 'public', 'data', 'channels'),
      where('participants', 'array-contains', uid)
    );
    const unsub1 = onSnapshot(q1, (snap) => {
      channelsPart = snap.docs.map((d) => ({ ...d.data(), id: d.id }));
      updateChannels();
    });

    const q2 = query(
      collection(db, 'artifacts', appId, 'public', 'data', 'channels'),
      where('createdBy', '==', uid)
    );
    const unsub2 = onSnapshot(q2, (snap) => {
      channelsCreated = snap.docs.map((d) => ({ ...d.data(), id: d.id }));
      updateChannels();
    });

    const docRef = doc(db, 'artifacts', appId, 'public', 'data', 'channels', 'general_chat_global');
    const unsub3 = onSnapshot(docRef, (docSnap) => {
      if (docSnap.exists()) {
        channelGlobal = [{ ...docSnap.data(), id: docSnap.id }];
      } else {
        channelGlobal = [];
      }
      updateChannels();
    });

    return () => {
      unsub1();
      unsub2();
      unsub3();
    };
  }, [user, isVault]);

  useEffect(() => {
    if (!isVault && !user) return;
    const uid = user?.uid || 'test-uid-123';

    let delPart = [];
    let delCreated = [];

    const updateDeleted = () => {
      const all = [...delPart, ...delCreated];
      const unique = Array.from(new Map(all.map((c) => [c.id, c])).values());
      unique.sort((a, b) => (b.deletedAt?.seconds || 0) - (a.deletedAt?.seconds || 0));
      setDeletedChannels(unique);
    };

    const q1 = query(
      collection(db, 'artifacts', appId, 'public', 'data', 'deleted_channels'),
      where('participants', 'array-contains', uid)
    );
    const unsub1 = onSnapshot(q1, (snap) => {
      delPart = snap.docs.map((d) => {
        const data = d.data();
        return { ...data, id: d.id, _originalId: data.id || d.id };
      });
      updateDeleted();
    });

    const q2 = query(
      collection(db, 'artifacts', appId, 'public', 'data', 'deleted_channels'),
      where('createdBy', '==', uid)
    );
    const unsub2 = onSnapshot(q2, (snap) => {
      delCreated = snap.docs.map((d) => {
        const data = d.data();
        return { ...data, id: d.id, _originalId: data.id || d.id };
      });
      updateDeleted();
    });

    return () => {
      unsub1();
      unsub2();
    };
  }, [user, isVault]);

  const taskChannelIdsStr = useMemo(() => {
    return channels.map((c) => c.id).sort().join(',');
  }, [channels]);

  useEffect(() => {
    if (!isVault && !user) return;
    const uid = user?.uid || 'test-uid-123';

    const cIds = taskChannelIdsStr.split(',').filter(Boolean);
    const validIds = [...new Set([...cIds, `personal-${uid}`])];

    const tasksMap = new Map();

    const unsubs = validIds.map((channelId) => {
      const q = query(
        collection(db, 'artifacts', appId, 'public', 'data', 'tasks'),
        where('channelId', '==', channelId)
      );
      return onSnapshot(q, (snap) => {
        const docs = snap.docs.map((d) => ({ ...d.data(), id: d.id }));
        tasksMap.set(channelId, docs);

        const all = Array.from(tasksMap.values()).flat();
        all.sort((a, b) => (a.order || 0) - (b.order || 0));
        setAllTasks(all);
      });
    });

    return () => unsubs.forEach((unsub) => unsub());
  }, [user, isVault, taskChannelIdsStr]);

  useEffect(() => {
    if (!isVault && !user) return;

    const q = query(
      collection(db, 'artifacts', appId, 'public', 'data', 'events'),
      where('createdBy', '==', user?.uid || 'test-uid-123')
    );
    const unsub = onSnapshot(q, (snap) => {
      const docs = snap.docs.map((d) => {
        const data = d.data();
        if (data.type === 'planning') data.type = 'tentative';
        return { ...data, id: d.id };
      });
      docs.sort((a, b) => {
        const dateA = a.startDate || '';
        const dateB = b.startDate || '';
        if (dateA !== dateB) return dateA.localeCompare(dateB);
        const timeA = a.startTime || '23:59:59';
        const timeB = b.startTime || '23:59:59';
        return timeA.localeCompare(timeB);
      });
      setEvents(docs);
    });

    return () => unsub();
  }, [user, isVault]);

  useEffect(() => {
    if (!isVault && !user) return;

    const q = query(collection(db, 'artifacts', appId, 'public', 'data', 'users'));
    const unsub = onSnapshot(q, (snap) => {
      if (import.meta.env.DEV) console.log('[DEBUG] onSnapshot(users) fired', snap.size);
      let all = snap.docs.map((d) => ({ ...d.data(), id: d.id }));
      // Filter out system/test accounts unless in Vault mode for automated tests
      if (!isVault) {
        all = all.filter((u) => {
          const n = (u.name || u.id || '').toLowerCase();
          if (n.includes('ghost')) return false;
          if (n.includes('tester')) return false;
          if (n.trim().toLowerCase() === 'user') return false;
          return true;
        });
      }
      stableSet(setAllUsers, all, true);
      setLoading(false);
    });

    return () => unsub();
  }, [user, isVault]);

  useEffect(() => {
    if (!isVault && !user) return;
    const uid = user?.uid || 'test-uid-123';

    const q = query(
      collection(db, 'artifacts', appId, 'public', 'data', 'photos'),
      where('createdBy', '==', uid)
    );
    const unsub = onSnapshot(q, (snap) => {
      const docs = snap.docs.map((d) => ({ ...d.data(), id: d.id }));
      docs.sort((a, b) => (b.createdAt?.seconds || 0) - (a.createdAt?.seconds || 0));
      setPhotos(docs);
    });
    return () => unsub();
  }, [user, isVault]);

  useEffect(() => {
    if (!isVault && !user) return;
    const uid = user?.uid || 'test-uid-123';

    const q = query(
      collection(db, 'artifacts', appId, 'public', 'data', 'media'),
      where('createdBy', '==', uid)
    );
    const unsub = onSnapshot(q, (snap) => {
      const docs = snap.docs.map((d) => ({ ...d.data(), id: d.id })).filter(d => d.type === 'audio');
      docs.sort((a, b) => (b.createdAt?.seconds || 0) - (a.createdAt?.seconds || 0));
      setAudioTracks(docs);
    });
    return () => unsub();
  }, [user, isVault]);


  useEffect(() => {
    if (!isVault && !user) return;

    let cancelled = false;

    const loadConcerts = async () => {
      try {
        const cache = JSON.parse(localStorage.getItem(CONCERTS_CACHE_KEY));
        if (Array.isArray(cache?.docs) && Date.now() - cache.ts < CONCERTS_CACHE_TTL_MS) {
          if (!cancelled) setSongkickConcerts(cache.docs);
          return;
        }
      } catch (e) {
        // Invalid or unavailable cache — fall through to Firestore.
      }

      try {
        const q = query(
          collection(db, 'artifacts', appId, 'public', 'data', 'concerts')
        );
        const snap = await getDocs(q);
        const docs = snap.docs.map((d) => ({ ...d.data(), id: d.id }));
        if (!cancelled) setSongkickConcerts(docs);
        try {
          localStorage.setItem(CONCERTS_CACHE_KEY, JSON.stringify({ ts: Date.now(), docs }));
        } catch (e) {
          // Quota exceeded — skip caching, harmless.
        }
      } catch (error) {
        console.error('Failed to fetch concerts:', error);
      }
    };

    loadConcerts();
    return () => {
      cancelled = true;
    };
  }, [user, isVault]);

  useEffect(() => {
    if (!isVault && !user) return;

    const q = query(
      collection(db, 'artifacts', appId, 'public', 'data', 'notifications'),
      orderBy('timestamp', 'desc'),
      limit(100)
    );
    const unsub = onSnapshot(q, (snap) => {
      const docs = snap.docs.map((d) => ({ ...d.data(), id: d.id }));
      docs.sort((a, b) => (b.timestamp?.seconds || 0) - (a.timestamp?.seconds || 0));
      // Only keep the most recent 100 to save memory
      setNotifications(docs.slice(0, 100));
    });
    return () => unsub();
  }, [user, isVault]);

  return useMemo(
    () => ({ channels, deletedChannels, allTasks, events, allUsers, habits, habitLogs, photos, audioTracks, songkickConcerts, notifications, loading }),
    [channels, deletedChannels, allTasks, events, allUsers, habits, habitLogs, photos, audioTracks, songkickConcerts, notifications, loading]
  );
};
